/* CELVIAN install enhancement. No service worker, storage, requests or navigation hooks. */
(function () {
  'use strict';
  if (window.CelvianPWA) return;

  var root = document.documentElement;
  var script = document.currentScript;
  var designMode = Boolean((script && script.dataset.designMode === 'true') ||
    (window.Shopify && window.Shopify.designMode));
  var displayMode = typeof window.matchMedia === 'function' ?
    window.matchMedia('(display-mode: standalone)') : null;
  var deferredPrompt = null;
  var prompting = false;
  var installed = false;
  var accepted = false;
  var activeSheet = null;

  function isStandalone() {
    return Boolean((displayMode && displayMode.matches) || window.navigator.standalone === true);
  }

  function getPlatform() {
    var nav = window.navigator;
    if (/iPad|iPhone|iPod/.test(nav.userAgent) ||
      ((nav.platform === 'MacIntel' || /Macintosh/.test(nav.userAgent)) && nav.maxTouchPoints > 1)) {
      return 'ios';
    }
    return /Android/i.test(nav.userAgent) ? 'android' : 'desktop';
  }

  function canInstall() {
    return Boolean(deferredPrompt && !prompting && !installed && !accepted && !isStandalone() && !designMode);
  }

  function getState() {
    return { standalone: isStandalone(), installed: installed, accepted: accepted,
      canInstall: canInstall(), prompting: prompting, platform: getPlatform(), designMode: designMode };
  }

  function publish() {
    root.dataset.celvianDisplayMode = isStandalone() ? 'standalone' : 'browser';
    window.dispatchEvent(new CustomEvent('celvian:pwa:change', { detail: getState() }));
  }

  function showInstructions(trigger) {
    if (isStandalone() && !designMode) return { outcome: 'standalone' };
    var detail = { trigger: trigger || null, platform: getPlatform(), designMode: designMode };
    window.dispatchEvent(new CustomEvent('celvian:pwa:instructions', { detail: detail }));
    return { outcome: 'instructions' };
  }

  // Call directly inside a click handler: no asynchronous work precedes prompt().
  async function install(trigger) {
    if (isStandalone() || installed) return { outcome: 'installed' };
    if (prompting) return { outcome: 'busy' };
    if (accepted) return { outcome: 'accepted' };
    if (!canInstall()) return showInstructions(trigger);

    var event = deferredPrompt;
    deferredPrompt = null; // A BeforeInstallPromptEvent is single-use.
    prompting = true;
    try {
      var promptResult = event.prompt();
      publish();
      await promptResult;
      var choice = await event.userChoice;
      accepted = Boolean(choice && choice.outcome === 'accepted');
      return { outcome: accepted ? 'accepted' : 'dismissed' };
    } catch (_error) {
      return { outcome: 'unavailable' };
    } finally {
      prompting = false;
      publish();
    }
  }

  window.CelvianPWA = Object.freeze({ canInstall: canInstall, install: install,
    isStandalone: isStandalone, getPlatform: getPlatform, getState: getState,
    showInstructions: showInstructions });

  window.addEventListener('beforeinstallprompt', function (event) {
    if (typeof event.prompt !== 'function') return;
    // Leave browser UI alone until the merchant actually adds an install section.
    if (designMode || isStandalone() || document.querySelector('celvian-web-app-install')) event.preventDefault();
    if (designMode || isStandalone() || installed || accepted || prompting) return;
    deferredPrompt = event;
    publish();
  });
  window.addEventListener('appinstalled', function () {
    installed = true;
    accepted = false;
    deferredPrompt = null;
    publish();
  });
  function displayChanged() {
    if (isStandalone()) deferredPrompt = null;
    publish();
  }
  if (displayMode) {
    if (typeof displayMode.addEventListener === 'function') displayMode.addEventListener('change', displayChanged);
    else if (typeof displayMode.addListener === 'function') displayMode.addListener(displayChanged);
  }
  window.addEventListener('pageshow', displayChanged);
  // BFCache restores must not leave a modal or a scroll lock behind.
  window.addEventListener('pagehide', function () { if (activeSheet) activeSheet.closeSheet(); });

  if (window.customElements && !window.customElements.get('celvian-web-app-install')) {
    class CelvianWebAppInstall extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.button = this.querySelector('[data-celvian-pwa-install]');
        this.dialog = this.querySelector('dialog');
        this.status = this.querySelector('[data-celvian-pwa-status]');
        if (!this.button || !this.dialog || !this.status) return;
        this.initialized = true;
        this.nativeLabel = this.button.textContent.trim();
        this.supportsDialog = typeof this.dialog.showModal === 'function';
        this.onChange = this.update.bind(this);
        this.onClick = this.handleClick.bind(this);
        this.onInstructions = (event) => {
          if (event.detail.trigger && this.contains(event.detail.trigger)) this.openSheet(event.detail.trigger);
        };
        this.onCancel = (event) => { event.preventDefault(); this.closeSheet(); };
        this.onKeydown = (event) => {
          if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.closeSheet(); }
          if (event.key !== 'Tab') return;
          var focusable = Array.from(this.dialog.querySelectorAll('button, a[href], [tabindex="0"]'))
            .filter((element) => !element.disabled && element.getClientRects().length);
          var first = focusable[0];
          var last = focusable[focusable.length - 1];
          if (!first) { event.preventDefault(); return; }
          if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
            event.preventDefault(); last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault(); first.focus();
          }
        };
        this.onPointerDown = (event) => { this.backdropStart = this.outsideDialog(event); };
        this.onDialogClick = (event) => {
          if (this.backdropStart && this.outsideDialog(event)) this.closeSheet();
          this.backdropStart = false;
        };
        this.onClose = this.releaseSheet.bind(this);
        this.onUnload = (event) => { if (event.target.contains(this)) this.closeSheet(); };
        this.addEventListener('click', this.onClick);
        this.dialog.addEventListener('cancel', this.onCancel);
        this.dialog.addEventListener('keydown', this.onKeydown);
        this.dialog.addEventListener('pointerdown', this.onPointerDown);
        this.dialog.addEventListener('click', this.onDialogClick);
        this.dialog.addEventListener('close', this.onClose);
        window.addEventListener('celvian:pwa:change', this.onChange);
        window.addEventListener('celvian:pwa:instructions', this.onInstructions);
        if (designMode) document.addEventListener('shopify:section:unload', this.onUnload);
        this.update();
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        this.closeSheet();
        this.removeEventListener('click', this.onClick);
        this.dialog.removeEventListener('cancel', this.onCancel);
        this.dialog.removeEventListener('keydown', this.onKeydown);
        this.dialog.removeEventListener('pointerdown', this.onPointerDown);
        this.dialog.removeEventListener('click', this.onDialogClick);
        this.dialog.removeEventListener('close', this.onClose);
        window.removeEventListener('celvian:pwa:change', this.onChange);
        window.removeEventListener('celvian:pwa:instructions', this.onInstructions);
        document.removeEventListener('shopify:section:unload', this.onUnload);
        this.initialized = false;
      }

      update() {
        var state = getState();
        var hide = !designMode && (state.standalone || state.installed);
        if (hide) this.closeSheet();
        this.hidden = hide;
        this.button.hidden = !this.supportsDialog || state.accepted;
        this.button.disabled = state.prompting;
        this.button.setAttribute('aria-busy', String(state.prompting));
        this.button.textContent = state.canInstall || state.platform === 'ios' ? this.nativeLabel : this.dataset.optionsLabel;
        this.querySelector('[data-celvian-pwa-fallback]').hidden = this.supportsDialog;
        this.querySelector('[data-celvian-pwa-editor]').hidden = !designMode;
        if (state.accepted) this.status.textContent = this.dataset.acceptedMessage;
      }

      async handleClick(event) {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('[data-celvian-pwa-close]')) { this.closeSheet(); return; }
        var button = event.target.closest('[data-celvian-pwa-install]');
        if (!button || !this.contains(button)) return;
        this.status.textContent = '';
        var result = designMode ? showInstructions(button) : await install(button);
        if (!this.isConnected) return;
        if (result.outcome === 'accepted') this.status.textContent = this.dataset.acceptedMessage;
        else if (result.outcome === 'dismissed') this.status.textContent = this.dataset.dismissedMessage;
        else if (result.outcome === 'unavailable') this.status.textContent = this.dataset.unavailableMessage;
      }

      openSheet(trigger) {
        if (!this.supportsDialog || this.hidden) return;
        if (activeSheet && activeSheet !== this) activeSheet.closeSheet();
        var platform = getPlatform();
        this.querySelectorAll('[data-celvian-pwa-platform]').forEach((element) => {
          element.hidden = element.dataset.celvianPwaPlatform !== platform;
        });
        this.returnFocus = trigger;
        try {
          this.dialog.showModal(); // Native top layer makes the rest of the document inert.
          activeSheet = this;
          root.classList.add('celvian-pwa-dialog-open');
          this.button.setAttribute('aria-expanded', 'true');
          this.dialog.querySelector('[data-celvian-pwa-close]').focus({ preventScroll: true });
        } catch (_error) {
          this.querySelector('[data-celvian-pwa-fallback]').hidden = false;
        }
      }

      outsideDialog(event) {
        if (event.target !== this.dialog) return false;
        var rect = this.dialog.getBoundingClientRect();
        return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      }

      releaseSheet() {
        // A native close event is queued; it can arrive after the sheet has reopened.
        if (this.dialog.open) return;
        if (activeSheet === this) {
          activeSheet = null;
          root.classList.remove('celvian-pwa-dialog-open');
        }
        this.button.setAttribute('aria-expanded', 'false');
        if (this.returnFocus && this.returnFocus.isConnected && !this.hidden && !activeSheet) {
          this.returnFocus.focus({ preventScroll: true });
        }
        this.returnFocus = null;
      }

      closeSheet() {
        if (this.dialog && this.dialog.open) this.dialog.close();
        this.releaseSheet();
      }
    }
    window.customElements.define('celvian-web-app-install', CelvianWebAppInstall);
  }
  publish();
}());
